import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { goToCoachProfile } from '../utils/coachNav';
import type { Lesson } from '../types/lesson';
import type { Order } from '../types/order';
import UploadNextLessonModal from './UploadNextLessonModal';
import AutoRenewSection from './AutoRenewSection';
import { DETAIL_COLUMN, DetailTitleRow } from './detailLayout';
import LessonStatusIcon from './LessonStatusIcon';
import { useUserIdentity } from '../hooks/useUserIdentity';
import { useUserPendingUploads } from '../hooks/usePendingUploads';
import { BTN_SECONDARY_SM_ON_PAGE } from './buttonStyles';
import { buildProgramSlots, type SlotState } from '../utils/programSlots';
import { SlotRow } from './programRow';
import { lessonStateCopy } from '../utils/lessonStateCopy';

/**
 * The player's training program.
 *
 * A list of the program's lessons, one row per slot the program bought. The
 * progress IS the list: where the player is in a four-lesson arc is legible
 * from four rows carrying their own state, so there is no separate stepper,
 * banner or counter repeating it.
 *
 * §8: rows on one white section, the section header outside it on the page
 * background, no bordered container inside another. Biscayne appears twice —
 * the Upload action and the status mark for a state that is genuinely active —
 * and nowhere as an outline around something static.
 *
 * Rows come from `order.totalLessons`, not from the lesson documents, because
 * documents are created on demand: a four-lesson program routinely holds one or
 * two. Slots with no document render as upcoming.
 *
 * The header is WaitingForLesson's, copied rather than approximated: same
 * sticky title row with the back button inline, same HEADER_GAP beneath it,
 * same 48 avatar linking to the coach's profile, same name treatment, same
 * Message pill. A player moving between a single lesson and a program should
 * not be able to tell that two components drew these.
 */

/** WaitingForLesson's rhythm: title -> name row -> status. Same constant. */
const HEADER_GAP = 16;


interface BundleProgressProps {
  order: Order;
  lessons: Lesson[];
  onLessonClick: (lesson: Lesson) => void;
  /** Opens the in-play lesson's detail — the same screen a single lesson or
   *  shot consult shows while it is with the coach. */
  onActiveLessonClick?: (lesson: Lesson) => void;
  onBack?: () => void;
  /** Opens the refund request. Live: Lessons.tsx passes it, and it is the
   *  player's only route to a refund. */
  onRequestRefund?: () => void;
  /** 'page' is the routed treatment with its own back button; 'panel' fills a
   *  two-pane container, where the list beside it is already the way back. */
  variant?: 'page' | 'panel';
}

export default function BundleProgress({
  order, lessons, onLessonClick, onActiveLessonClick, onBack, onRequestRefund, variant = 'page',
}: BundleProgressProps) {
  const isPanel = variant === 'panel';
  /* The slot's NUMBER and its lesson id. The number alone was not enough:
     without the id the upload modal could not pass existingLessonId, so
     uploading to a slot whose lesson already exists created a SECOND lesson
     with the same lessonNumber instead of filling the reserved one. The order
     then carried two lesson 1s, buildProgramSlots' byNumber map kept whichever
     sorted last, and the pane showed the orphaned reserved copy as "Ready to
     submit" while the list showed the real one as "Awaiting analysis". */
  const [uploadSlot, setUploadSlot] = useState<{ number: number; lessonId?: string } | null>(null);
  const { uploads, hasBlockingUpload } = useUserPendingUploads();

  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const liveCoach = useUserIdentity(order.coachId, 'coach');
  const coachName = liveCoach?.name ?? order.coachName ?? 'Coach';
  const coachFirstName = coachName.split(' ')[0] || 'your coach';
  const coachAvatarUrl = liveCoach?.avatarUrl ?? order.coachAvatarUrl;
  const initials = coachName.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || 'C';


  const inFlight = uploads.filter((u) => ['uploading', 'uploaded', 'processing'].includes(u.status));
  const slots = buildProgramSlots({
    order,
    lessons,
    hasBlockingUpload,
    pendingFor: (lessonId) => {
      const u = inFlight.find((x) => x.existingLessonId === lessonId || x.lessonId === lessonId);
      return u ? { status: u.status, progress: u.uploadProgress ?? 0 } : null;
    },
    pendingUnattached: (() => {
      const u = inFlight.find((x) => !x.lessonId && !x.existingLessonId);
      return u ? { status: u.status, progress: u.uploadProgress ?? 0 } : null;
    })(),
  });

  const deliveredCount = slots.filter((s) => s.state === 'ready_to_watch' || s.state === 'complete').length;

  /* Several states can be true at once — a delivered lesson sitting unwatched
     while the next is open for upload. This is the order they lead in: the
     newest event first, then whatever the player's turn is. `complete` only
     leads when every slot is watched; otherwise the unwatched one does. */
  const PRIORITY: SlotState[] = [
    'refunded', 'uploading', 'processing', 'awaiting_review', 'ready_to_watch', 'ready_to_submit',
  ];
  const leading = PRIORITY.map((st) => slots.find((sl) => sl.state === st)).find(Boolean)
    ?? (slots.length > 0 && slots.every((sl) => sl.state === 'complete') ? slots[slots.length - 1] : undefined);
  const status = leading
    ? lessonStateCopy(leading.state, {
      coachFirstName,
      lessonNumber: leading.number,
      totalLessons: slots.length,
    })
    : null;

  return (
    <div className={`flex flex-col h-full overflow-y-auto${isPanel ? '' : ' bg-db-bg'}`}>
      <div className={`py-6 md:py-8 pt-5 md:pt-8 ${DETAIL_COLUMN}`}>

        {/* The title always renders; only the back button is conditional. That
            is the reference's rule, and it is why the panel variant used to
            open on a floating avatar with no heading at all. */}
        <DetailTitleRow title="Training Program" onBack={isPanel ? undefined : onBack} sticky={!isPanel} />

        {/* One row at every width, as the reference. The name carries no second
            line: "Lesson 1 of 4" is what the list below says, once. */}
        <div className="flex flex-row items-center justify-between gap-3" style={{ marginTop: HEADER_GAP }}>
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              title={coachName || 'Coach'}
              onClick={() => goToCoachProfile(order.coachId, currentUser?.uid ?? undefined)}
              className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden text-[16px] font-bold text-white flex-shrink-0 border-none p-0 cursor-pointer"
              /* Oasis, not biscayne: biscayne is for active and selected, and a
                 coach's avatar is neither. This is the reference's fill. */
              style={{ background: '#54C3EA' }}
            >
              {coachAvatarUrl
                ? <img src={coachAvatarUrl} alt={coachName} className="w-full h-full object-cover" />
                : initials}
            </button>
            {/* Same shape as WaitingForLesson's, which has a date beside the
                name. This one has nothing to stack, but the structure matches
                so the two cannot drift. */}
            <div className="min-w-0 flex flex-col md:flex-row md:items-center md:gap-x-3">
              <span className="text-[14px] leading-[20px] text-[#1C1C1C] truncate">{coachName}</span>
            </div>
          </div>

          <button
            data-testid="message-coach-btn"
            onClick={() => navigate(order.conversationId ? `/inbox/${order.conversationId}` : '/inbox')}
            className={`${BTN_SECONDARY_SM_ON_PAGE} flex-shrink-0`}
          >
            <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Message {coachFirstName}
          </button>
        </div>

        {/* ── Status ───────────────────────────────────────────────────
            WaitingForLesson's block, same geometry: HEADER_GAP above it, the
            icon and label on one row at gap 8, the paragraph 6 beneath. It
            extends downward from the coach row; nothing above it moves. */}
        {status && (
          <div style={{ marginTop: HEADER_GAP }}>
            <div className="flex items-center" style={{ gap: 8 }}>
              <LessonStatusIcon label={status.label} />
              <span className="text-[13px] leading-[18px] font-semibold text-[#1C1C1C]">{status.label}</span>
            </div>
            <p className="md:hidden text-[13px] leading-[18px] text-[rgba(60,60,67,0.6)]" style={{ marginTop: 6 }}>
              {status.short}
            </p>
            <p className="hidden md:block max-w-[520px] text-[13px] text-db-text-2" style={{ marginTop: 6 }}>
              {status.long}
            </p>
          </div>
        )}

        {/* §8.2: the header sits on the page background, above the section. */}
        <div
          className="text-[13px] leading-[18px] font-semibold uppercase text-[rgba(60,60,67,0.6)]"
          style={{ letterSpacing: '0.5px', marginTop: 32, marginBottom: 16 }}
        >
          Lessons
        </div>
        <div style={{ background: '#FFFFFF', borderRadius: 12, overflow: 'hidden' }}>
          {slots.map((slot, i) => (
            <SlotRow
              key={slot.number}
              slot={slot}
              first={i === 0}
              /* Delivered goes to the delivered lesson, unchanged. The one in
                 play goes to the same detail a single lesson shows while it is
                 with the coach. A slot with no document has nothing to open. */
              onOpen={
                !slot.lesson ? undefined
                  : slot.state === 'ready_to_watch' || slot.state === 'complete'
                    ? () => onLessonClick(slot.lesson!)
                    : slot.state === 'awaiting_review' && onActiveLessonClick
                      ? () => onActiveLessonClick(slot.lesson!)
                      : undefined
              }
              action={slot.state === 'ready_to_submit'
                ? { label: 'Upload', onClick: () => setUploadSlot({ number: slot.number, lessonId: slot.lesson?.id }), testId: 'upload-video-btn' }
                : undefined}
            />
          ))}
        </div>

        {/* Auto renew, at the end of the program it renews. Gated inside the
            component, so a program that must not see it renders nothing and no
            caller has to know the rule. Never on a single lesson or a shot
            consult: this screen is the only place it mounts. */}
        <AutoRenewSection order={order} orderLessons={lessons} className="mt-8" />

        {/* The one thing here besides the list. It is the player's only route
            to a refund and it is gated to the window they are entitled to it —
            after lesson 1 is delivered — so it cannot live on the row for a
            lesson that has not happened. A quiet footnote under the section,
            not a row inside it. */}
        {deliveredCount === 1 && onRequestRefund && (
          <button
            data-testid="request-refund-btn"
            onClick={onRequestRefund}
            className="mt-4 text-[13px] leading-[18px] text-[rgba(60,60,67,0.6)] hover:text-[#1C1C1C] bg-transparent border-none p-0 cursor-pointer transition-colors"
          >
            Request a Refund
          </button>
        )}
      </div>

      {uploadSlot !== null && (
        <UploadNextLessonModal
          order={order}
          nextLessonNum={uploadSlot.number}
          /* Fills the reserved lesson rather than creating a duplicate.
             Undefined when the slot has no lesson yet, which is the lazy-create
             case and is correct. */
          existingLessonId={uploadSlot.lessonId}
          onClose={() => setUploadSlot(null)}
        />
      )}
    </div>
  );
}
